
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ReorderSettingParam } from "@/models/reorder_setting_param";
import { getParameterLabel } from "@/config/parameterLabels";

// Interface to track which fields are checked for force update
interface CheckedFields {
  leadtime: boolean;
  orderCycle: boolean;
  minStockCoverDays: boolean;
}

@Component
export default class GlobalParamForm extends Vue {
  @Prop({ default: () => new ReorderSettingParam() })
  public initReorderSettingParam!: ReorderSettingParam;

  @Prop({ default: false })
  public saveLoading!: boolean;

  @Prop({ default: '' })
  public formLevelWarning!: string;

  public reorderSettingParam: ReorderSettingParam = new ReorderSettingParam();
  public isFormValid: boolean = false;
  public forceUpdate: boolean = false;

  // Track which fields are checked for force update
  public checkedFields: CheckedFields = {
    leadtime: false,
    orderCycle: false,
    minStockCoverDays: false,
  };

  public numberRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || "Value must be a non-negative number",
  ];

  // Computed: check if any field is checked for force update
  get hasAnyCheckedFields(): boolean {
    return this.checkedFields.leadtime || this.checkedFields.orderCycle || this.checkedFields.minStockCoverDays;
  }

  // Computed properties for standardized labels
  get leadTimeLabel(): string {
    return getParameterLabel('LEAD_TIME', 'label');
  }

  get leadTimeHint(): string {
    return getParameterLabel('LEAD_TIME', 'hint');
  }

  get orderCycleLabel(): string {
    return getParameterLabel('ORDER_CYCLE', 'label');
  }

  get orderCycleHint(): string {
    return getParameterLabel('ORDER_CYCLE', 'hint');
  }

  get safetyStockDaysLabel(): string {
    return getParameterLabel('SAFETY_STOCK_DAYS', 'label');
  }

  get safetyStockDaysHint(): string {
    return getParameterLabel('SAFETY_STOCK_DAYS', 'hint');
  }

  @Watch("initReorderSettingParam", { immediate: true, deep: true })
  private onInitChanged() {
    this.reorderSettingParam = new ReorderSettingParam(this.initReorderSettingParam);

    // Reset forceUpdate and checked fields when form is reinitialized
    this.forceUpdate = false;
    this.resetCheckedFields();
    // Clear warning when initial data changes (e.g., on load)
    this.$emit('update:form-level-warning', '');
  }

  @Watch("forceUpdate")
  private onForceUpdateChanged(newVal: boolean) {
    if (!newVal) {
      // Reset checked fields when force update is disabled
      this.resetCheckedFields();
    }
  }

  private resetCheckedFields() {
    this.checkedFields = {
      leadtime: false,
      orderCycle: false,
      minStockCoverDays: false,
    };
  }

  public async applySetting() {
    const form = this.$refs.form as any;
    if (form && form.validate()) {
      // Clear previous warning
      this.$emit('update:form-level-warning', '');

      if (this.forceUpdate) {
        // Force Update Mode: use checked fields
        if (!this.hasAnyCheckedFields) {
          this.$emit('update:form-level-warning', 'Please select at least one parameter to force update.');
          return;
        }

        // Create param object with only checked fields
        const checkedParams = new ReorderSettingParam();

        if (this.checkedFields.leadtime) {
          checkedParams.leadtime = this.reorderSettingParam.leadtime;
        }
        if (this.checkedFields.orderCycle) {
          checkedParams.orderCycle = this.reorderSettingParam.orderCycle;
        }
        if (this.checkedFields.minStockCoverDays) {
          checkedParams.minStockCoverDays = this.reorderSettingParam.minStockCoverDays;
        }

        this.$emit("apply-setting", {
          params: checkedParams,
          forceUpdate: true
        });
      } else {
        // Normal Mode: send all parameters (applies to products without custom settings)
        const params = new ReorderSettingParam();
        params.leadtime = this.reorderSettingParam.leadtime;
        params.orderCycle = this.reorderSettingParam.orderCycle;
        params.minStockCoverDays = this.reorderSettingParam.minStockCoverDays;

        this.$emit("apply-setting", {
          params,
          forceUpdate: false
        });
      }
    }
  }
}
